import React, { useState, useEffect, useMemo } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { IconButton, TextField, Tooltip, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import TanstackTable from 'components/elements/advance-table/TanstackTable';
import RefreshButton from 'components/elements/refresh-button/RefreshButton';
import { getGeneratedVideosAPI } from 'ServerAPI/serverAPI';
import actionIcon from "../../assets/images/course/action-icon.svg";
import { useToast } from 'context/toast-context/ToastProvider';
import { DateDisplay } from 'helper/utils';
import { useNavigate } from 'react-router-dom';

// VideoGenerationTable Component
export const VideoGenerationTable = ({ userId, setVideoCount }) => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // State management
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch data function
    const fetchData = async (isRefresh = false) => {
        try {
            if (isRefresh) {
                setIsRefreshing(true);
            } else {
                setLoading(true);
            }
            setError(null);

            if (!userId) {
                showToast('User ID not found in cookies', "error");
                return;
            }

            const response = await getGeneratedVideosAPI(userId);
            // const response = {
            //     data: {
            //         videos: [
            //             {
            //                 videoId: "ece915d522a247f898ae25a2ddfc0677",
            //                 title: "uploadingToS3",
            //                 organizationId: "781bb6da-7908-41e4-86ab-8676bc085c56",
            //                 status: "completed",
            //                 s3_key: "generated_videos/781bb6da-7908-41e4-86ab-8676bc085c56/ece915d522a247f898ae25a2ddfc0677.mp4",
            //                 createdBy: "65046d6c-feec-47fc-ab4e-1a39f261c8a2",
            //                 createdAt: "2025-08-05T19:37:17.022532"
            //             }
            //         ]
            //     }
            // };

            if (response.data && response.data.videos) {
                setData(response.data.videos);
                setVideoCount((response.data.videos).length);
            } else {
                setData([]);
            }
        } catch (err) {
            console.error('Error fetching generated videos:', err);
            setError(err.message || 'Failed to fetch generated videos');
            setData([]);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchData();
    }, [userId]);

    // Handle refresh
    const handleRefresh = () => {
        fetchData(true);
    };

    // Handle search
    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    // Handle generate new video
    const handleGenerateNewVideo = () => {
        navigate('/video-generation/');
    };

    // Handle view video
    const handleViewVideo = (videoId) => {
        navigate(`/video-generation/${videoId}`);
    };

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;

        return data.filter(item =>
            item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.status?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [data, searchTerm]);

    // Table columns configuration
    const columns = useMemo(() => [
        {
            id: 'sno',
            header: 'S.No.',
            cell: ({ row }) => row.index + 1,
            enableSorting: false,
        },
        {
            accessorKey: 'title',
            header: 'Title',
            cell: ({ getValue }) => (
                <div className="fw-semibold text-truncate" style={{ maxWidth: '200px' }}>
                    {getValue() || 'N/A'}
                </div>
            ),
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ getValue }) => {
                const status = getValue() || 'unknown';
                const statusColors = {
                    completed: 'success',
                    processing: 'warning',
                    failed: 'danger',
                    inProgress: 'secondary'
                };
                return (
                    <span className={`badge bg-${statusColors[status] || 'secondary'}`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                );
            },
        },
        {
            accessorKey: 'createdAt',
            header: 'Created At',
            cell: ({ getValue }) => (
                <span className="text-muted small">
                    <DateDisplay timeString={getValue()} />
                </span>
            ),
        },
        {
            accessorKey: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <Tooltip title="View Video">
                    <IconButton>
                        <span
                            onClick={() => {
                                handleViewVideo(row.original.videoId);
                            }}
                            style={{ display: 'flex', alignItems: 'center' }}
                        >
                            <img src={actionIcon} alt="action icon" />
                        </span>
                    </IconButton>
                </Tooltip>
            ),
            enableSorting: false,
        },
    ], []);

    // Render loading state
    if (loading && !isRefreshing) {
        return (
            <div className="text-center p-5">
                <CircularProgress />
                <div className="mt-2">Loading videos...</div>
            </div>
        );
    }

    return (
        <>
            {/* Search and Action Bar */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search for title, status"
                    InputProps={{ startAdornment: <SearchIcon /> }}
                    onChange={handleSearch}
                    value={searchTerm}
                    className="w-100"
                    style={{ maxWidth: '400px' }}
                />
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <RefreshButton
                        handleRefresh={handleRefresh}
                        title="Refresh Videos"
                        isRefreshing={isRefreshing}
                    />
                    <Button
                        variant="primary"
                        onClick={handleGenerateNewVideo}
                        className="d-flex align-items-center gap-1"
                    >
                        <AddIcon fontSize="small" />
                        Generate New Video
                    </Button>
                </div>
            </div>

            {/* Error State */}
            {error && (
                <Alert variant="danger" className="mb-4">
                    <Alert.Heading>Error</Alert.Heading>
                    <p className="mb-0">{error}</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => fetchData()}
                        >
                            Try Again
                        </Button>
                    </div>
                </Alert>
            )}

            {/* Content Area */}
            <div className="mt-3">
                {!error && (
                    <>
                        {filteredData.length === 0 ? (
                            <div className="text-center p-5">
                                <div className="mb-3">
                                    <i className="fas fa-video fa-3x text-muted"></i>
                                </div>
                                <h5 className="text-muted">
                                    {searchTerm ? 'No videos found matching your search' : 'No generated videos found'}
                                </h5>
                                <p className="text-muted">
                                    {searchTerm
                                        ? 'Try adjusting your search terms or clear the search to see all videos.'
                                        : 'Get started by generating your first video.'
                                    }
                                </p>
                                {!searchTerm && (
                                    <Button
                                        variant="primary"
                                        onClick={handleGenerateNewVideo}
                                        className="mt-2"
                                    >
                                        <AddIcon fontSize="small" className="me-1" />
                                        Generate New Video
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <TanstackTable
                                data={filteredData}
                                columns={columns}
                                pagination={true}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
};